import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ThreeDMapService,
  TicketTypeWithBlockId,
} from 'src/app/services/three-dmap.service';
import { Product } from '../../types/product.type';
import { UtilService } from '../../services/util.service';
import { Subscription } from 'rxjs';
import {
  SeatItem,
  ThreedSeatItemComponent,
} from './components/threed-seat-item.component';

export type BitmapChangeEventData = {
  row_tracker_id: number;
  str_rbitmap: string;
  str_blocked_rbitmap: string;
  rbitmap: number;
};

@Component({
  selector: 'app-threed-seats-picker',
  standalone: true,
  imports: [CommonModule, ThreedSeatItemComponent],
  templateUrl: './threed-seats-picker.component.html',
  styleUrls: ['./threed-seats-picker.component.css'],
})
export class ThreedSeatsPickerComponent implements OnInit, OnDestroy {
  @Input() public isOpen = false;

  @Input() public ticketTypes: TicketTypeWithBlockId[] = [];

  @Output() public handleClose = new EventEmitter<boolean>();

  @Output() public goToCheckout = new EventEmitter();

  @Output() public productEventEmitter = new EventEmitter<Product>();

  private products: Product[] = [];
  public seats: SeatItem[] = [];
  private selectedSeatSubscription!: Subscription;
  private isPanoViewSubscription!: Subscription;

  public isPanoViewActive = false;

  constructor(
    public readonly threeDMapService: ThreeDMapService,
    private readonly utilService: UtilService,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.mountSelectedSeatListener();
    this.mountSeatsTakenListener();
    this.mountIsPanoViewListener();
  }

  ngOnDestroy(): void {
    this.unmountSelectedSeatListener();
    this.unmountSeatsTakenListener();
    this.unmountIsPanoViewListener();

    document.body.style.overflow = 'auto';
  }

  openMap() {
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
    this.threeDMapService.init(this.ticketTypes);
  }

  closeMap() {
    this.isOpen = false;
    this.clearProducts();
    document.body.style.overflow = 'auto';
  }

  onClose() {
    this.handleClose.emit(false);
  }

  goBack() {
    this.threeDMapService.displayTopView();
  }

  onAccept() {
    this.goToCheckout.emit();
  }

  private clearProducts() {
    let p: Product | undefined
    while ((p = this.products.pop())) {
      p.qty = 0;
      p.seats = [];
      this.productEventEmitter.emit(p)
    }
    this.seats = []
  }

  private unmountSelectedSeatListener() {
    this.selectedSeatSubscription.unsubscribe();
  }

  private mountSelectedSeatListener() {
    this.selectedSeatSubscription =
      this.threeDMapService.selectedSeat.subscribe((data) => {
        if (!data || !this.isOpen) return;
        const tt = this.getTicketTypeByBlockId(data.blockId)!;
        const rt = this.getRowTrackerByPrefix(tt, data.prefix)!;
        const p = this.getProductByTicketTypeOrCreate(tt.id);

        this.ngZone.run(() => {
          const seatItem = {
            price: tt.price,
            block: tt.name.replace('_', ' '),
            seat: rt.prefix.replace(/\w+_/, '') + data.seatIndex,
          };

          if (data.selected) {
            this.seats.push(seatItem);
          } else {
            const seatItemIndex = this.seats.findIndex(
              (s) => s.seat === seatItem.seat && s.block === s.block
            );
            this.seats.splice(seatItemIndex, 1);
          }
        });

        if (data.selected) {
          p.qty++;
          p.seats?.push({
            tracker_id: rt.id,
            seat_index: data.seatIndex,
          });
        } else {
          const seatProductIndex = p.seats?.findIndex(
            (s) => s.seat_index === data.seatIndex && s.tracker_id === rt.id
          )!;
          p.qty--;
          p.seats?.splice(seatProductIndex, 1);
        }
        this.productEventEmitter.emit(p);
      });
  }

  private unmountIsPanoViewListener() {
    this.isPanoViewSubscription.unsubscribe();
  }

  private mountIsPanoViewListener() {
    this.isPanoViewSubscription = this.isPanoViewSubscription =
      this.threeDMapService.isPanoViewActive.subscribe((isActive) => {
        this.ngZone.run(() => {
          this.isPanoViewActive = !!isActive;
        });
      });
  }

  private unmountSeatsTakenListener() {
    this.ticketTypes.forEach((tt) => {
      const key = this.getRowsNotificationBitmapEventListenerKey(tt.id);
      this.utilService.getSocket().removeAllListeners(key);
    });
  }

  private mountSeatsTakenListener() {
    this.ticketTypes.forEach((tt) => {
      const key = this.getRowsNotificationBitmapEventListenerKey(tt.id);
      this.utilService.getSocket().on(key, (data: BitmapChangeEventData) => {
        const rt = tt.rows_trackers.find((rt) => rt.id === data.row_tracker_id);
        if (!rt) return;

        rt.str_rbitmap = data.str_rbitmap;
        rt.str_blocked_rbitmap = data.str_blocked_rbitmap;

        this.threeDMapService.updateTracker(tt.block_id, rt);
      });
    });
  }

  private getTicketTypeByBlockId(blockId: string) {
    return this.ticketTypes.find((t) => t.block_id === blockId);
  }

  private getRowTrackerByPrefix(tt: TicketTypeWithBlockId, prefix: string) {
    return tt.rows_trackers.find((r) => r.prefix === prefix);
  }

  private getProductByTicketTypeOrCreate(ticketTypeId: number): Product {
    const p = this.products.find((p) => p.ticket_type_id === ticketTypeId);
    if (!p) {
      this.products.push({ ticket_type_id: ticketTypeId, seats: [], qty: 0 });
    }

    return p || this.products[this.products.length - 1];
  }

  private getRowsNotificationBitmapEventListenerKey(ticketTypeId: number) {
    return `rows_${ticketTypeId}_notification_rbitmap`;
  }
}
